/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { Browser, browserCheck, CORE_CONFIG } from '@dm/core';
import { FeatureConfig } from 'app/config/feature';

import { AppModule } from './app/app.module';
import { environment } from './environments/environment';

// 浏览器语言
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
(function setLanguage(): void {
    const LANGUAGE_KEY = CORE_CONFIG.languageKey;
    const getLanguageFromLocation = (): string | null => {
        if (!window.location.search) {
            return null;
        }

        // window.location.search开头有一个问号
        const search = window.location.search.substring(1);
        const queries = search.split('&');
        for (const query of queries) {
            const pair = query.split('=');
            // 当前只支持一种中文，zh_CN转为zh
            if (pair[0] === LANGUAGE_KEY) {
                return pair[1].substring(0, 2);
            }
        }
        return null;
    };

    const getFormLocalStorage = (key: string): string => {
        const rawValue = window.localStorage.getItem(key);
        let res;
        try {
            res = JSON.parse(rawValue);
        } catch (e) {
            res = rawValue;
        }

        return res;
    };

    // 支持url中指定language设置系统语言
    let language = getLanguageFromLocation();
    if (language) {
        window.localStorage.setItem(LANGUAGE_KEY, language);
    }

    language = getFormLocalStorage(LANGUAGE_KEY);

    if (!language) {
        language = navigator.language || (navigator as any).browserLanguage;
        const support_ja_lan = FeatureConfig.IsSupportJaLan;
        if (language.substring(0, 2) === 'zh') {
            language = 'zh';
        } else if (support_ja_lan && language.substring(0, 2) === 'ja') {
            language = 'ja';
        } else {
            language = 'en';
        }

        window.localStorage.setItem(LANGUAGE_KEY, language);
    }

    const textContentMap = new Map([
        ['zh', '加载中...'],
        ['en', 'Loading...'],
        ['ja', '読み込み中......'],
    ]);

    document.getElementById('loadingText').textContent = textContentMap.get(language);
})();

// 检测浏览器版本。不能放到app.module中，否则在IE9、10上执行不到就报错了
browserCheck();

if (Browser.name === 'ie' && Browser.version < 11) {
    window.location.href = location.protocol + '//' + location.host + '/assets/static/page-error-ie.html';
} else if (Browser.name === 'chrome' && Browser.version < 65 ||
    Browser.name === 'firefox' && Browser.version < 55 ||
    Browser.name === 'safari' && Browser.version < 9) {
    window.location.href = location.protocol + '//' + location.host + '/assets/static/page-error.html';
}

if (environment.production) {
    enableProdMode();

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const voidFunc = (val: any): void => {};

    // 去掉console.log打印
    if (window.console) {
        window.console.log = voidFunc;
    } else {
        // 兼容IE
        if (console) {
            console.log = voidFunc;
        }
    }
}

platformBrowserDynamic().bootstrapModule(AppModule)
    .catch(() => {});
